import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { BehaviorSubject, catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { Firestore, collection, doc, getDoc, getDocs, query, updateDoc, where } from '@angular/fire/firestore';
import { Item } from '../models/item.model';
import { UserProfile } from '../models/profile.model';
import { Functions, httpsCallable } from '@angular/fire/functions';

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private functions = inject(Functions);
  private userProfileSubject = new BehaviorSubject<UserProfile | null>(null);
  userProfile$ = this.userProfileSubject.asObservable();
  private genericFilterItems = httpsCallable<any, any>(this.functions, 'genericFilterItems');


  constructor(private firestore: Firestore, private injector: Injector) {
    this.functions.region = 'europe-west3';
  }

  getUserOpportunities(userId: string, lastDoc: string | null = null, limit: number = 20): Observable<{ items: Item[], totalCount: number }> {

    return from(this.genericFilterItems({
      collection: 'LookForsAndOffers',
      filters: [
        { field: 'author.authorId', operator: '==', value: userId }
      ],
      sorts: [{ field: 'createdAt', direction: 'desc' }],
      lastDoc: lastDoc,
      limit: limit
    })).pipe(
      map(response => {
        const items = (response.data?.items || []).map((item: any) => ({
          ...item,
          id: item['id'],
          subscribers: item['subscribers'] || [],
          author: item['author'] || {
            authorEmail: '',
            authorId: '',
            authorName: ''
          },
          editedBy: item['editedBy'] || {
            editorEmail: '',
            editorId: '',
            editorName: ''
          },
          workPackage: item['workPackage'] || [],
          documentValues: item['documentValues'] || {},
          isCv: item['isCv'] || false,
          headcount: item['headcount'] || 0
        })) as Item[];
        return { items, totalCount: response.data?.totalCount || 0 };
      }),
      catchError(error => {
        console.error('Error fetching user opportunities:', error);
        return of({ items: [], totalCount: 0 });
      })
    );
  }

  getUserSubscriptions(userId: string, lastDoc: any = null, limit: number = 20): Observable<{ items: Item[], totalCount: number }> {
    const applicantsRef = collection(this.firestore, 'Applicants');
    const q = query(applicantsRef, where('userId', '==', userId));

    return from(runInInjectionContext(this.injector, async () => {
      const snapshot = await getDocs(q);

      const allOpportunityIds = snapshot.docs.map(doc => doc.data()['opportunityId']);
      if (allOpportunityIds.length === 0) return { items: [], totalCount: 0 };

      const start = lastDoc ? allOpportunityIds.indexOf(lastDoc) + 1 : 0;
      const pagedIds = allOpportunityIds.slice(start, start + limit);

      const response = await this.genericFilterItems({
        collection: 'LookForsAndOffers',
        filters: [{ field: '__name__', operator: 'in', value: pagedIds }]
      });

      const items = (response.data?.items || []).map((item: any) => ({
        ...item,
        id: item['id'],
        subscribers: item['subscribers'] || [],
        author: item['author'] || { authorEmail: '', authorId: '', authorName: '' },
        workPackage: item['workPackage'] || [],
        documentValues: item['documentValues'] || {},
        isCv: item['isCv'] || false,
        headcount: item['headcount'] || 0
      }));

      return { items, totalCount: allOpportunityIds.length };
    })).pipe(
      catchError(error => {
        console.error('Error fetching user subscriptions:', error);
        return of({ items: [], totalCount: 0 });
      })
    );
  }

  loadUserProfile(userId: string): Observable<UserProfile | null> {
    return from(runInInjectionContext(this.injector, () =>
      getDoc(doc(this.firestore, 'Users', userId))
    )).pipe(
      map(userDoc => {
        const profile = userDoc.exists() ? userDoc.data() as UserProfile : null;
        this.userProfileSubject.next(profile);
        return profile;
      }),
      catchError(error => {
        console.error('Error loading user profile:', error);
        return of(null);
      })
    );
  }

  async updateUserProfile(userId: string, profile: Partial<UserProfile>): Promise<void> {
    const updatedProfile = {
      ...this.userProfileSubject.value,
      ...profile,
      lastUpdated: new Date().toISOString()
    };
    try {
      const userDocRef = doc(this.firestore, 'Users', userId);
      await updateDoc(userDocRef, updatedProfile);
      this.userProfileSubject.next(updatedProfile as UserProfile);
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  getSelectedItemsString(items: { name: string }[]): string {
    return items.map(item => item.name).join(', ');
  }
}
